// Admin v2 — Jótékonyság kezelés (Figma ADM_11, 11b–11e).
// Kampányok: charity_campaigns (a mobilapp sémája: name, description, goal, raised, status, startDate, endDate, beneficiary, region).
// Kedvezményezettek, bevétel-hozzárendelés és audit napló: app_settings/charity dokumentum.
// Az adományok a mobilapp XP-felajánlásai (xp_transactions, sub: 'felajánlás').
import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc, arrayUnion, collection, deleteDoc, doc, getDocs, limit, onSnapshot, query, serverTimestamp, setDoc, Timestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtDate, fmtInt, toDate } from '../data';
import {
  Badge, Button, Card, ConfirmModal, Empty, Field, IconTile, Input, Modal, PageHeader, SectionTitle, Select, Spinner, Tabs, TextArea, Tone, useToast,
} from '../ui';

type Tab = 'campaigns' | 'beneficiaries' | 'allocation' | 'audit';
type CStatus = 'draft' | 'active' | 'completed' | 'paused';
interface Campaign { id: string; name: string; description: string; goal: number; raised: number; status: CStatus; start: Date | null; end: Date | null; beneficiary: string; region: string }
interface Beneficiary { id: string; name: string; city: string; description: string; contact: string; email: string; verified: boolean }
interface AuditRow { at: Date; action: string; user: string; campaign: string; amount: string }
interface CharityDoc { beneficiaries: Beneficiary[]; allocation: Record<string, number>; audit: AuditRow[] }

const STATUS: Record<CStatus, { t: string; tone: Tone }> = {
  active: { t: 'Aktív', tone: 'green' }, completed: { t: 'Befejezett', tone: 'gray' }, draft: { t: 'Piszkozat', tone: 'gold' }, paused: { t: 'Szüneteltetve', tone: 'gold' },
};
const ALLOC = [
  ['marketplace', 'Marketplace értékesítés'], ['subscriptions', 'Előfizetések'], ['events', 'Esemény belépők'],
  ['voicePacks', 'Voice Pack értékesítés'], ['motionPacks', 'Motion Pack értékesítés'],
] as const;
const ACTION_TONE: Record<string, Tone> = { 'Adomány jóváírva': 'green', 'Kampány módosítva': 'cyan', 'Kampány létrehozva': 'cyan', 'Kampány törölve': 'red', 'Kifizetés': 'gold', 'Arány módosítva': 'purple', 'Kedvezményezett módosítva': 'cyan' };

const CH_REF = () => doc(db, 'app_settings', 'charity');

export default function CharityPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('campaigns');
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [ch, setCh] = useState<CharityDoc>({ beneficiaries: [], allocation: {}, audit: [] });
  const [donations, setDonations] = useState<AuditRow[]>([]);
  const [edit, setEdit] = useState<Campaign | 'new' | null>(null);
  const [del, setDel] = useState<Campaign | null>(null);
  const [benEdit, setBenEdit] = useState<Beneficiary | 'new' | null>(null);
  const myName = user?.displayName || 'Admin';

  useEffect(() => onSnapshot(collection(db, 'charity_campaigns'), (s) => setCampaigns(s.docs.map((d) => {
    const v = d.data();
    return {
      id: d.id, name: String(v.name ?? 'Kampány'), description: String(v.description ?? ''), goal: Number(v.goal ?? 0), raised: Number(v.raised ?? 0),
      status: (['draft', 'active', 'completed', 'paused'].includes(v.status) ? v.status : 'active') as CStatus,
      start: toDate(v.startDate), end: toDate(v.endDate), beneficiary: String(v.beneficiary ?? ''), region: String(v.region ?? ''),
    };
  }).sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))), () => setCampaigns([])), []);

  useEffect(() => onSnapshot(CH_REF(), (s) => {
    const v = s.data() ?? {};
    setCh({
      beneficiaries: (v.beneficiaries as Beneficiary[]) ?? [],
      allocation: (v.allocation as Record<string, number>) ?? {},
      audit: ((v.audit as Record<string, unknown>[]) ?? []).map((a) => ({ at: toDate(a.at) ?? new Date(0), action: String(a.action ?? ''), user: String(a.user ?? ''), campaign: String(a.campaign ?? ''), amount: String(a.amount ?? '–') })),
    });
  }, () => undefined), []);

  useEffect(() => {
    getDocs(query(collection(db, 'xp_transactions'), where('sub', '==', 'felajánlás'), limit(300))).then((s) => setDonations(s.docs.map((d) => {
      const v = d.data();
      return { at: toDate(v.createdAt) ?? new Date(0), action: 'Adomány jóváírva', user: 'rendszer', campaign: String(v.description ?? ''), amount: `${fmtInt(Math.abs(Number(v.amount ?? 0)))} XP` };
    }))).catch(() => undefined);
  }, []);

  const log = (action: string, campaign: string, amount = '–') =>
    setDoc(CH_REF(), { audit: arrayUnion({ at: Timestamp.now(), action, user: myName, campaign, amount }) }, { merge: true }).catch(() => undefined);

  const right = tab === 'campaigns' || tab === 'beneficiaries' ? <Button icon="Plus" onClick={() => setEdit('new')}>Új kampány</Button> : null;

  return (
    <div>
      <PageHeader title="Jótékonyság kezelés" sub="Kampányok, kedvezményezettek és bevétel-hozzárendelés" right={right} />
      <Tabs<Tab> value={tab} onChange={setTab} options={[
        { value: 'campaigns', label: 'Kampányok' }, { value: 'beneficiaries', label: 'Kedvezményezettek' },
        { value: 'allocation', label: 'Bevétel konfiguráció' }, { value: 'audit', label: 'Audit napló' },
      ]} />

      {tab === 'campaigns' && (campaigns === null ? <Spinner /> : campaigns.length === 0 ? <Card><Empty>Még nincs kampány.</Empty></Card> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {campaigns.map((c) => {
            const pct = c.goal ? Math.min(100, Math.round((c.raised / c.goal) * 100)) : 0;
            return (
              <Card key={c.id} className="!p-5">
                <div className="flex items-start gap-3">
                  <IconTile icon="HandHeart" tone="purple" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold text-white truncate">{c.name}</div>
                    <div className="text-[12px] text-[#8a8f99] truncate">{[c.beneficiary, c.region].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                  <Badge tone={STATUS[c.status].tone}>{STATUS[c.status].t}</Badge>
                </div>
                <div className="flex justify-between mt-4 text-[12px]"><span className="text-[#8a8f99]">Előrehaladás</span><span className="text-[#00AFF2] font-semibold">{pct}%</span></div>
                <div className="h-2 mt-1.5 rounded-full bg-[#31383D] overflow-hidden"><div className="h-full rounded-full bg-[#00AFF2]" style={{ width: `${pct}%` }} /></div>
                <div className="mt-3 text-[13px] font-semibold text-white">{fmtInt(c.raised)} / {fmtInt(c.goal)} XP</div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button kind="ghost" icon="PencilSimpleLine" className="!h-[32px] !text-[13px]" onClick={() => setEdit(c)}>Szerkesztés</Button>
                  <button type="button" onClick={() => setDel(c)} className="inline-flex items-center gap-1.5 h-[32px] px-3 rounded-full border border-[#ef4444] text-[#ef4444] text-[13px] font-semibold hover:bg-[#ef4444]/10"><Icon name="Trash" size={14} />Törlés</button>
                </div>
              </Card>
            );
          })}
        </div>
      ))}

      {tab === 'beneficiaries' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[15px] font-extrabold italic uppercase text-white">Kedvezményezett kezelés</h2>
            <Button icon="Plus" onClick={() => setBenEdit('new')}>Új kedvezményezett</Button>
          </div>
          {ch.beneficiaries.length === 0 ? <Card><Empty>Még nincs kedvezményezett.</Empty></Card> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {ch.beneficiaries.map((b) => (
                <Card key={b.id} className="!p-5">
                  <div className="flex items-start gap-3">
                    <IconTile icon="HandHeart" tone="cyan" size={40} />
                    <div className="flex-1 min-w-0"><div className="text-[14px] font-semibold text-white">{b.name}</div><div className="text-[12px] text-[#8a8f99]">{b.city}</div></div>
                    {b.verified && <Badge tone="green">Ellenőrizve</Badge>}
                  </div>
                  <p className="mt-3 text-[13px] text-[#B8B8C3]">{b.description}</p>
                  <div className="mt-3 pt-3 border-t border-[#31383D] text-[12px]">
                    <div className="flex justify-between py-1"><span className="text-[#8a8f99] inline-flex items-center gap-1.5"><Icon name="UserCircle" size={14} />Kapcsolattartó</span><span className="text-white">{b.contact || '—'}</span></div>
                    <div className="flex justify-between py-1"><span className="text-[#8a8f99] inline-flex items-center gap-1.5"><Icon name="Envelope" size={14} />Email</span><span className="text-white">{b.email || '—'}</span></div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button kind="ghost" icon="PencilSimpleLine" className="!h-[32px] !text-[13px]" onClick={() => setBenEdit(b)}>Szerkesztés</Button>
                    <button type="button" onClick={async () => {
                      await setDoc(CH_REF(), { beneficiaries: ch.beneficiaries.filter((x) => x.id !== b.id) }, { merge: true });
                      log('Kedvezményezett módosítva', b.name); toast('Kedvezményezett törölve.');
                    }} className="inline-flex items-center gap-1.5 h-[32px] px-3 rounded-full border border-[#ef4444] text-[#ef4444] text-[13px] font-semibold hover:bg-[#ef4444]/10"><Icon name="Trash" size={14} />Törlés</button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'allocation' && <AllocationTab value={ch.allocation} onSave={async (a) => {
        await setDoc(CH_REF(), { allocation: a }, { merge: true });
        const changed = ALLOC.filter(([k]) => (ch.allocation[k] ?? 0) !== a[k]).map(([k, l]) => `${l}: ${ch.allocation[k] ?? 0}% → ${a[k]}%`);
        if (changed.length) log('Arány módosítva', changed.join(', '));
        toast('Bevétel-hozzárendelés mentve.');
      }} />}

      {tab === 'audit' && <AuditTab rows={[...ch.audit, ...donations]} />}

      {edit && <CampaignModal c={edit === 'new' ? null : edit} beneficiaries={ch.beneficiaries} onClose={() => setEdit(null)}
        onSaved={(name, created) => { log(created ? 'Kampány létrehozva' : 'Kampány módosítva', name); toast('Kampány mentve.'); }} />}
      {benEdit && <BeneficiaryModal b={benEdit === 'new' ? null : benEdit} onClose={() => setBenEdit(null)} onSave={async (b) => {
        const list = benEdit === 'new' ? [...ch.beneficiaries, b] : ch.beneficiaries.map((x) => (x.id === b.id ? b : x));
        await setDoc(CH_REF(), { beneficiaries: list }, { merge: true });
        log('Kedvezményezett módosítva', b.name); toast('Kedvezményezett mentve.');
      }} />}
      {del && (
        <ConfirmModal title="Kampány törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)}
          text={<>Biztosan törlöd: <b className="text-white">{del.name}</b>? A kampány eltűnik a mobilappból.</>}
          onConfirm={async () => { try { await deleteDoc(doc(db, 'charity_campaigns', del.id)); log('Kampány törölve', del.name); toast('Kampány törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); } setDel(null); }} />
      )}
    </div>
  );
}

function AllocationTab({ value, onSave }: { value: Record<string, number>; onSave: (a: Record<string, number>) => Promise<void> }) {
  const [a, setA] = useState<Record<string, number>>(() => Object.fromEntries(ALLOC.map(([k]) => [k, value[k] ?? 0])));
  useEffect(() => setA(Object.fromEntries(ALLOC.map(([k]) => [k, value[k] ?? 0]))), [value]);
  const [busy, setBusy] = useState(false);
  return (
    <Card className="!p-5">
      <SectionTitle right={<Button icon="Check" disabled={busy} onClick={async () => { setBusy(true); await onSave(a); setBusy(false); }}>Mentés</Button>}>Bevétel hozzárendelés</SectionTitle>
      <p className="text-[13px] text-[#B8B8C3] mb-4">Állítsd be, hogy az egyes tranzakciótípusok hány százaléka megy jótékonysági kampányokba.</p>
      <div className="flex flex-col gap-2.5">
        {ALLOC.map(([k, label]) => (
          <div key={k} className="grid grid-cols-[200px_1fr_90px_auto] items-center gap-4 px-4 py-3 rounded-xl bg-[#1d232a]">
            <span className="text-[13px] font-semibold text-white">{label}</span>
            <input type="range" min={0} max={30} value={a[k]} onChange={(e) => setA((x) => ({ ...x, [k]: Number(e.target.value) }))} className="accent-[#00AFF2]" />
            <Input inputMode="numeric" value={`${a[k]}`} onChange={(e) => setA((x) => ({ ...x, [k]: Math.min(100, Number(e.target.value.replace(/[^\d]/g, '')) || 0) }))} right={<span className="text-[13px] text-[#8a8f99]">%</span>} />
            <span className="text-[12px] text-[#8a8f99] whitespace-nowrap">megy jótékonyságra</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AuditTab({ rows }: { rows: AuditRow[] }) {
  const [q, setQ] = useState('');
  const [month, setMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((r) => (!month || `${r.at.getFullYear()}-${String(r.at.getMonth() + 1).padStart(2, '0')}` === month)
      && (!t || [r.action, r.user, r.campaign].some((f) => f.toLowerCase().includes(t)))).sort((a, b) => b.at.getTime() - a.at.getTime());
  }, [rows, q, month]);
  const csv = () => {
    const data = [['Időpont', 'Művelet', 'Felhasználó', 'Kampány', 'Összeg'], ...list.map((r) => [`${fmtDate(r.at)} ${r.at.toTimeString().slice(0, 5)}`, r.action, r.user, r.campaign, r.amount])];
    const s = '﻿' + data.map((x) => x.map((c) => `"${c.replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const el = document.createElement('a'); el.href = URL.createObjectURL(new Blob([s], { type: 'text/csv;charset=utf-8' })); el.download = `jotekonysag-naplo-${month}.csv`; el.click(); URL.revokeObjectURL(el.href);
  };
  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés művelet vagy felhasználó alapján..." className="flex-1 min-w-[260px]" />
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[190px] [color-scheme:dark]" icon="CalendarBlank" />
        <Button kind="ghost" icon="DownloadSimple" onClick={csv}>Exportálás CSV</Button>
      </div>
      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead><tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
            <th className="pl-5 pr-4 h-[39px] font-semibold">Időpont</th><th className="px-4 font-semibold">Művelet</th><th className="px-4 font-semibold">Felhasználó</th><th className="px-4 font-semibold">Kampány</th><th className="px-4 font-semibold">Összeg</th>
          </tr></thead>
          <tbody>
            {list.slice(0, 200).map((r, i) => (
              <tr key={i} className="border-t border-[#31383D] h-[46px] text-[13px]">
                <td className="pl-5 pr-4 font-semibold text-white">{fmtDate(r.at)}. {r.at.toTimeString().slice(0, 5)}</td>
                <td className="px-4"><Badge tone={ACTION_TONE[r.action] ?? 'gray'}>{r.action}</Badge></td>
                <td className="px-4 text-[#B8B8C3]">{r.user}</td>
                <td className="px-4 text-[#B8B8C3]">{r.campaign || '—'}</td>
                <td className="px-4 text-[#B8B8C3]">{r.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <Empty>Nincs naplóbejegyzés ebben az időszakban.</Empty>}
      </div>
    </div>
  );
}

const iso = (d: Date | null) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');

function CampaignModal({ c, beneficiaries, onClose, onSaved }: { c: Campaign | null; beneficiaries: Beneficiary[]; onClose: () => void; onSaved: (name: string, created: boolean) => void }) {
  const toast = useToast();
  const [f, setF] = useState({
    name: c?.name ?? '', description: c?.description ?? '', goal: String(c?.goal ?? ''), status: c?.status ?? 'draft' as CStatus,
    start: iso(c?.start ?? new Date()), end: iso(c?.end ?? null), beneficiary: c?.beneficiary ?? '', region: c?.region ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !Number(f.goal)) { toast('A név és a célösszeg kötelező.', 'red'); return; }
    setBusy(true);
    const data = {
      name: f.name.trim(), description: f.description.trim(), goal: Number(f.goal), status: f.status, beneficiary: f.beneficiary, region: f.region.trim(),
      startDate: f.start ? Timestamp.fromDate(new Date(`${f.start}T00:00:00`)) : Timestamp.now(),
      endDate: f.end ? Timestamp.fromDate(new Date(`${f.end}T23:59:59`)) : null, updatedAt: serverTimestamp(),
    };
    try {
      if (c) await updateDoc(doc(db, 'charity_campaigns', c.id), data);
      else await addDoc(collection(db, 'charity_campaigns'), { ...data, raised: 0, createdAt: serverTimestamp() });
      onSaved(f.name.trim(), !c);
      onClose();
    } catch { toast('A mentés nem sikerült.', 'red'); }
    setBusy(false);
  };
  return (
    <Modal title={c ? 'Kampány szerkesztése' : 'Új kampány'} onClose={onClose} width={600}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={busy} onClick={save}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Kampány neve" className="col-span-2"><Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="pl. Karácsonyi jótékonysági akció 2026" /></Field>
        <Field label="Leírás" className="col-span-2"><TextArea rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Írd le a kampány céljait és célkitűzéseit..." /></Field>
        <Field label="Célösszeg (XP)"><Input inputMode="numeric" value={f.goal} onChange={(e) => set('goal', e.target.value.replace(/[^\d]/g, ''))} placeholder="1 000 000" /></Field>
        <Field label="Állapot"><Select value={f.status} onChange={(v) => set('status', v)} options={[{ value: 'draft', label: 'Piszkozat' }, { value: 'active', label: 'Aktív' }, { value: 'paused', label: 'Szüneteltetve' }, { value: 'completed', label: 'Befejezett' }]} /></Field>
        <Field label="Kezdési dátum"><Input type="date" value={f.start} onChange={(e) => set('start', e.target.value)} className="[color-scheme:dark]" /></Field>
        <Field label="Befejezési dátum"><Input type="date" value={f.end} onChange={(e) => set('end', e.target.value)} className="[color-scheme:dark]" /></Field>
        <Field label="Kedvezményezett"><Select value={f.beneficiary} onChange={(v) => set('beneficiary', v)} options={[{ value: '', label: 'Válassz kedvezményezett szervezetet' }, ...beneficiaries.map((b) => ({ value: b.name, label: b.name })), ...(f.beneficiary && !beneficiaries.some((b) => b.name === f.beneficiary) ? [{ value: f.beneficiary, label: f.beneficiary }] : [])]} /></Field>
        <Field label="Régió"><Input value={f.region} onChange={(e) => set('region', e.target.value)} placeholder="pl. Budapest, Magyarország" /></Field>
      </div>
    </Modal>
  );
}

function BeneficiaryModal({ b, onClose, onSave }: { b: Beneficiary | null; onClose: () => void; onSave: (b: Beneficiary) => Promise<void> }) {
  const [f, setF] = useState<Beneficiary>(b ?? { id: `b_${Date.now()}`, name: '', city: '', description: '', contact: '', email: '', verified: false });
  const set = (k: keyof Beneficiary, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Modal title={b ? 'Kedvezményezett szerkesztése' : 'Új kedvezményezett'} onClose={onClose} width={520}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!f.name.trim()} onClick={async () => { await onSave({ ...f, name: f.name.trim() }); onClose(); }}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Szervezet neve" className="col-span-2"><Input value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Város"><Input value={f.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label="Kapcsolattartó"><Input value={f.contact} onChange={(e) => set('contact', e.target.value)} /></Field>
        <Field label="Email" className="col-span-2"><Input icon="Envelope" value={f.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Leírás" className="col-span-2"><TextArea rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} /></Field>
      </div>
      <label className="mt-4 flex items-center gap-2.5 text-[13px] text-white">
        <input type="checkbox" checked={f.verified} onChange={(e) => set('verified', e.target.checked)} className="accent-[#2bc48a] w-4 h-4" />Ellenőrzött szervezet
      </label>
    </Modal>
  );
}
